/**
 * Billing Zustand Store
 *
 * Manages all state for the Billing section including:
 * - Revenue metrics and dashboard data
 * - Invoices list with filters
 * - Past-due accounts
 * - Payment disputes
 * - Usage overages
 */
import { create } from 'zustand';
import { getApiClient, API_ENDPOINTS } from '@respectly/api-client';
import type {
  BillingDashboard,
  Invoice,
  InvoiceListFilters,
  PastDueAccount,
  PaymentDispute,
  UsageOverage,
  PaginationMeta,
  FreePlanAccount,
} from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface BillingError {
  code: string;
  message: string;
}

// ============================================
// State Interface
// ============================================

export interface BillingState {
  // Dashboard
  dashboard: BillingDashboard | null;
  isLoadingDashboard: boolean;
  dashboardError: BillingError | null;

  // Invoices
  invoices: Invoice[];
  invoicesPagination: PaginationMeta | null;
  invoiceFilters: InvoiceListFilters;
  isLoadingInvoices: boolean;
  invoicesError: BillingError | null;

  // Past Due
  pastDueAccounts: PastDueAccount[];
  isLoadingPastDue: boolean;

  // Disputes
  disputes: PaymentDispute[];
  isLoadingDisputes: boolean;

  // Overages
  overages: UsageOverage[];
  isLoadingOverages: boolean;

  // Actions - Dashboard
  loadDashboard: () => Promise<void>;

  // Actions - Invoices
  loadInvoices: (page?: number) => Promise<void>;
  setInvoiceFilter: <K extends keyof InvoiceListFilters>(
    key: K,
    value: InvoiceListFilters[K]
  ) => void;
  clearInvoiceFilters: () => void;

  // Actions - Past Due
  loadPastDueAccounts: () => Promise<void>;

  // Actions - Disputes
  loadDisputes: () => Promise<void>;

  // Actions - Overages
  loadOverages: () => Promise<void>;

  // Free Plan Accounts
  freePlanAccounts: FreePlanAccount[];
  isLoadingFreePlanAccounts: boolean;
  freePlanAccountsError: BillingError | null;

  // Actions - Free Plan Accounts
  loadFreePlanAccounts: () => Promise<void>;

  // Utility
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const getInitialState = () => ({
  // Dashboard
  dashboard: null as BillingDashboard | null,
  isLoadingDashboard: false,
  dashboardError: null as BillingError | null,

  // Invoices
  invoices: [] as Invoice[],
  invoicesPagination: null as PaginationMeta | null,
  invoiceFilters: {} as InvoiceListFilters,
  isLoadingInvoices: false,
  invoicesError: null as BillingError | null,

  // Past Due
  pastDueAccounts: [] as PastDueAccount[],
  isLoadingPastDue: false,

  // Disputes
  disputes: [] as PaymentDispute[],
  isLoadingDisputes: false,

  // Overages
  overages: [] as UsageOverage[],
  isLoadingOverages: false,

  // Free Plan Accounts
  freePlanAccounts: [] as FreePlanAccount[],
  isLoadingFreePlanAccounts: false,
  freePlanAccountsError: null as BillingError | null,
});

// ============================================
// Store Implementation
// ============================================

export const useBillingStore = create<BillingState>((set, get) => ({
  ...getInitialState(),

  // ============================================
  // Dashboard Actions
  // ============================================

  loadDashboard: async () => {
    set({ isLoadingDashboard: true, dashboardError: null });
    try {
      const api = getApiClient();
      const response = await api.get<BillingDashboard>(API_ENDPOINTS.SUPER_ADMIN.BILLING.DASHBOARD);

      set({
        dashboard: response,
        isLoadingDashboard: false,
      });
    } catch (error) {
      set({
        isLoadingDashboard: false,
        dashboardError: {
          code: 'LOAD_DASHBOARD_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load dashboard',
        },
      });
    }
  },

  // ============================================
  // Invoice Actions
  // ============================================

  loadInvoices: async (page = 1) => {
    set({ isLoadingInvoices: true, invoicesError: null });
    try {
      const api = getApiClient();
      const { invoiceFilters } = get();
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('limit', '20');

      // Apply filters
      if (invoiceFilters.search) params.set('search', invoiceFilters.search);
      if (invoiceFilters.status) params.set('status', invoiceFilters.status);
      if (invoiceFilters.tenantId) params.set('tenantId', invoiceFilters.tenantId);
      if (invoiceFilters.dateRange && invoiceFilters.dateRange !== 'all') {
        params.set('dateRange', invoiceFilters.dateRange);
      }

      const response = await api.get<{ data: Invoice[]; pagination: PaginationMeta }>(
        `${API_ENDPOINTS.SUPER_ADMIN.BILLING.INVOICES}?${params.toString()}`
      );

      set({
        invoices: response.data,
        invoicesPagination: response.pagination,
        isLoadingInvoices: false,
      });
    } catch (error) {
      set({
        isLoadingInvoices: false,
        invoicesError: {
          code: 'LOAD_INVOICES_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load invoices',
        },
      });
    }
  },

  setInvoiceFilter: (key, value) => {
    set((state) => ({
      invoiceFilters: { ...state.invoiceFilters, [key]: value },
    }));
    get().loadInvoices(1);
  },

  clearInvoiceFilters: () => {
    set({ invoiceFilters: {} });
    get().loadInvoices(1);
  },

  // ============================================
  // Past Due Actions
  // ============================================

  loadPastDueAccounts: async () => {
    set({ isLoadingPastDue: true });
    try {
      const api = getApiClient();
      const response = await api.get<PastDueAccount[]>(API_ENDPOINTS.SUPER_ADMIN.BILLING.PAST_DUE);

      set({
        pastDueAccounts: Array.isArray(response) ? response : [],
        isLoadingPastDue: false,
      });
    } catch {
      set({ isLoadingPastDue: false });
    }
  },

  // ============================================
  // Dispute Actions
  // ============================================

  loadDisputes: async () => {
    set({ isLoadingDisputes: true });
    try {
      const api = getApiClient();
      const response = await api.get<PaymentDispute[]>(API_ENDPOINTS.SUPER_ADMIN.BILLING.DISPUTES);

      set({
        disputes: Array.isArray(response) ? response : [],
        isLoadingDisputes: false,
      });
    } catch {
      set({ isLoadingDisputes: false });
    }
  },

  // ============================================
  // Overage Actions
  // ============================================

  loadOverages: async () => {
    set({ isLoadingOverages: true });
    try {
      const api = getApiClient();
      const overages = await api.get<UsageOverage[]>(API_ENDPOINTS.SUPER_ADMIN.BILLING.USAGE_OVERAGES);

      set({
        overages: Array.isArray(overages) ? overages : [],
        isLoadingOverages: false,
      });
    } catch {
      set({ isLoadingOverages: false });
    }
  },

  // ============================================
  // Free Plan Accounts Actions
  // ============================================

  loadFreePlanAccounts: async () => {
    set({ isLoadingFreePlanAccounts: true, freePlanAccountsError: null });
    try {
      const api = getApiClient();
      const response = await api.get<FreePlanAccount[]>(
        API_ENDPOINTS.SUPER_ADMIN.BILLING.FREE_PLAN_ACCOUNTS
      );
      set({
        freePlanAccounts: Array.isArray(response) ? response : [],
        isLoadingFreePlanAccounts: false,
      });
    } catch (error) {
      set({
        isLoadingFreePlanAccounts: false,
        freePlanAccountsError: {
          code: 'LOAD_FREE_PLAN_ACCOUNTS_FAILED',
          message: error instanceof Error ? error.message : 'Failed to load free plan accounts',
        },
      });
    }
  },

  // ============================================
  // Utility Actions
  // ============================================

  clearError: () => {
    set({
      dashboardError: null,
      invoicesError: null,
    });
  },

  reset: () => {
    set(getInitialState());
  },
}));

// ============================================
// Selector Hooks
// ============================================

/** Get total past due amount */
export const usePastDueTotal = () => {
  return useBillingStore((state) =>
    state.pastDueAccounts.reduce((sum, acc) => sum + acc.amountDue, 0)
  );
};

/** Get disputes needing response */
export const useDisputesNeedingResponse = () => {
  return useBillingStore((state) =>
    state.disputes.filter((d) => d.status === 'needs_response')
  );
};

/** Get past due accounts sorted by days overdue */
export const usePastDueSorted = () => {
  return useBillingStore((state) =>
    [...state.pastDueAccounts].sort((a, b) => b.daysOverdue - a.daysOverdue)
  );
};

/** Check if any billing data is loading */
export const useBillingLoading = () => {
  const isLoadingDashboard = useBillingStore((state) => state.isLoadingDashboard);
  const isLoadingInvoices = useBillingStore((state) => state.isLoadingInvoices);
  return isLoadingDashboard || isLoadingInvoices;
};

/** Get active invoice filters count */
export const useActiveInvoiceFiltersCount = () => {
  return useBillingStore((state) => {
    let count = 0;
    if (state.invoiceFilters.search) count++;
    if (state.invoiceFilters.status) count++;
    if (state.invoiceFilters.tenantId) count++;
    if (state.invoiceFilters.dateRange && state.invoiceFilters.dateRange !== 'all') count++;
    return count;
  });
};

/** Get free plan accounts sorted by longest time on free plan */
export const useFreePlanAccountsSorted = () =>
  useBillingStore((s) =>
    [...s.freePlanAccounts].sort((a, b) => b.daysOnFreePlan - a.daysOnFreePlan)
  );
